import { useEffect, useState } from "react";
import { Flame, MapPin } from "lucide-react";
import api from "@/lib/api";

/**
 * HotRightNow — top-3 trending pickup zones in the last 60 minutes.
 * Pure social proof: passengers see "the platform is moving" before they
 * book; drivers (on the driver page) see "demand is heating up here".
 *
 * Hidden when there's nothing to show — no fake empty state.
 */
export default function HotRightNow({ minutes = 60, limit = 3, onPickBucket }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/aria/map-intelligence/hot-now",
          { params: { minutes, limit } });
        if (!cancelled) setItems(res.data?.items || []);
      } catch { /* silent — best-effort polish */ }
    };
    load();
    const t = setInterval(load, 90_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [minutes, limit]);

  if (!items.length) return null;

  return (
    <div data-testid="hot-right-now"
      className="p-3 border border-orange-500/30 bg-orange-500/[0.07] rounded-md">
      <div className="flex items-center gap-2 mb-2">
        <Flame size={14} className="text-orange-400" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-orange-300">
          Hot Right Now · last {minutes} min
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {items.map((z, i) => (
          <button
            key={`${z.lat}-${z.lng}`}
            data-testid={`hot-zone-${i}`}
            onClick={() => onPickBucket?.(z)}
            className="text-left p-2.5 bg-black/30 border border-white/10 rounded hover:border-orange-400/40 transition-colors"
          >
            <div className="flex items-center justify-between text-[10px] text-orange-300/80 uppercase tracking-wider mb-1">
              <span className="inline-flex items-center gap-1">
                <MapPin size={10} /> Zone #{i + 1}
              </span>
              <span>{z.count} rides</span>
            </div>
            <p className="text-xs text-white/80 font-display">
              ({z.lat.toFixed(3)}, {z.lng.toFixed(3)})
            </p>
            <div className="mt-1.5 h-1 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-orange-400"
                style={{ width: `${z.freshness_pct}%` }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
